import { FormField, FormRenderTypes } from "../Shared/Components/FormRenderer";
import { FormRenderGroupTypes } from "../Shared/Components/FormRenderer/FormRenderGroup.types";
import { Reservation } from './Types/reservationTypes';
import {
  DISCOUNT_AMOUNT_MIN_THRESHOLD,
  MONEY_AMOUNT_MIN_THRESHOLD,
} from './Constants/reservationConstants';

export const isDiscountAmountDisabled = (totalPriceOverride: unknown): boolean => {
  const normalizedTotalPriceOverride = Number(totalPriceOverride);
  return (
    Number.isFinite(normalizedTotalPriceOverride) &&
    normalizedTotalPriceOverride > 0
  );
};

export const reservationFields: FormField<Reservation>[] = [
  {
    name: 'createdAt',
    label: ' Utworzono',
    type: FormRenderTypes.date,
    value: '',
    group: FormRenderGroupTypes.basic,
    disabled: true,
  },
  {
    name: 'bookingId',
    label: ' Wybierz rezerwację',
    type: FormRenderTypes.autocomplete,
    value: '',
    group: FormRenderGroupTypes.basic,
  },
  {
    name: 'applicantId',
    label: 'Wybierz zgłaszającego',
    type: FormRenderTypes.autocomplete,
    value: '',
    group: FormRenderGroupTypes.basic,
    // Zachowane w schemacie dla przejściowej kompatybilności; celowo ukryte w podstawowym UI.
    hidden: true,
    placeholder: 'Wybierz zgłaszającego',
    helperText:
      'Pole zachowane dla przejściowej kompatybilności. W razie tymczasowego włączenia wybierz osobę lub podmiot zgłaszający rezerwację.',
  },
  {
    name: 'applicantPartyId',
    label: 'Strona zgłaszająca',
    type: FormRenderTypes.autocomplete,
    value: '',
    group: FormRenderGroupTypes.basic,
    placeholder: 'Wybierz stronę dla roli zgłaszającego',
    helperText: 'Wymagane. Wybierz formalną stronę rezerwacji. Jeśli nie ma jej w bazie, wybierz dodanie nowej strony.',
  },
  {
    name: 'invoiceBuyerPartyId',
    label: 'Strona faktury',
    type: FormRenderTypes.autocomplete,
    value: '',
    group: FormRenderGroupTypes.basic,
    placeholder: 'Domyślnie jak ApplicantParty, jeśli puste',
    helperText: 'Wypełnij tylko, jeśli nabywca faktury jest inny niż strona zgłaszająca. Gdy zostawisz puste, użyjemy strony zgłaszającej.',
  },
  {
    name: 'fundingPartyId',
    label: 'Strona finansująca',
    type: FormRenderTypes.autocomplete,
    value: '',
    group: FormRenderGroupTypes.basic,
    placeholder: 'Opcjonalne źródło finansowania',
    helperText: 'Wypełnij tylko, jeśli koszt rezerwacji pokrywa inny podmiot niż strona zgłaszająca lub nabywca faktury.',
  },
  {
    name: 'offerBoardingPlaceId',
    label: 'Wybierz miejsce wsiadania',
    type: FormRenderTypes.autocomplete,
    value: '',
    group: FormRenderGroupTypes.basic,
    placeholder: 'Miejsce wsiadania można wybrac tylko po wybraniu rezerwacji',
  },
  {
    name: 'declaredParticipantsCount',
    label: 'Deklarowana liczba uczestników',
    type: FormRenderTypes.number,
    value: 1,
    group: FormRenderGroupTypes.basic,
    placeholder: 'Wymagane, gdy lista uczestników jest pusta',
  },
  {
    name: 'totalPriceOverride',
    label: 'Cena całkowita (grupowa)',
    type: FormRenderTypes.number,
    value: '',
    group: FormRenderGroupTypes.basic,
    placeholder: 'Np. 50000',
    inputProps: {
      step: MONEY_AMOUNT_MIN_THRESHOLD,
      min: MONEY_AMOUNT_MIN_THRESHOLD,
    },
    helperText:
      'Użyj dla rezerwacji grupowych z wynegocjowaną ceną stałą. Nadpisuje kalkulację: cena jedn. × uczestnicy.',
  },
  {
    name: 'discountAmount',
    label: 'Rabat',
    type: FormRenderTypes.number,
    value: '',
    group: FormRenderGroupTypes.basic,
    placeholder: 'Np. 50',
    inputProps: {
      step: DISCOUNT_AMOUNT_MIN_THRESHOLD,
      min: DISCOUNT_AMOUNT_MIN_THRESHOLD,
    },
    helperText:
      'Ręczny rabat od ceny kalkulowanej. Ignorowany gdy aktywna "Cena całkowita".',
  },
  {
    name: 'selectedOfferOptions',
    label: 'Opcje dodatkowe',
    type: FormRenderTypes.offerOptions,
    value: [],
    group: FormRenderGroupTypes.basic,
    hidden: true,
  },

  {
    name: 'participants',
    label: 'Uczestnicy',
    type: FormRenderTypes.dynamicList,
    value: [],
    itemFields: [
      {
        name: 'firstName',
        label: 'Imię',
        type: FormRenderTypes.input,
        value: '',
        placeholder: 'Wprowadź imię uczestnika',
      },
      {
        name: 'lastName',
        label: 'Nazwisko',
        type: FormRenderTypes.input,
        value: '',
        placeholder: 'Wprowadź nazwisko uczestnika',
      },
      {
        name: 'email',
        label: 'Email',
        type: FormRenderTypes.email,
        value: '',
        placeholder: 'Wprowadź email uczestnika',
        sx: { width: 300 },
      },
      {
        name: 'phoneNumber',
        label: 'Numer telefonu',
        type: FormRenderTypes.input,
        value: '',
        placeholder: 'Wprowadź numer telefonu uczestnika',
      },
      {
        name: 'birthDate',
        label: 'Data urodzenia',
        type: FormRenderTypes.date,
        value: '',
      },
      {
        name: 'address',
        label: 'Adres',
        type: FormRenderTypes.input,
        value: '',
        sx: { width: 300 },
      },
      {
        name: 'city',
        label: 'Miasto',
        type: FormRenderTypes.input,
        value: '',
      },
      {
        name: 'zipCode',
        label: 'Kod pocztowy',
        type: FormRenderTypes.input,
        value: '',
      },
    ],
    group: FormRenderGroupTypes.participants,
  },
];

export const applicantFields: FormField<any>[] = [
	{
		name: "applicant.firstName",
		label: "Imię",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.lastName",
		label: "Nazwisko",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.email",
		label: "Email",
		type: FormRenderTypes.email,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.phone",
		label: "Telefon",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.street",
		label: "Ulica",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.city",
		label: "Miasto",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.zipCode",
		label: "Kod pocztowy",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
	{
		name: "applicant.type",
		label: "Typ",
		type: FormRenderTypes.input,
		value: "",
		group: FormRenderGroupTypes.applicant,
	},
];
